/**
 * Vite fires `vite:preloadError` on window when a lazily-imported chunk — the
 * JS module or the CSS it pulls in — fails to load. In production that means
 * either a tab holding a stale index.html whose hashed assets a deploy has
 * since replaced, or a client that simply cannot fetch the asset (blocked
 * network, extension, scanner).
 *
 * Reloading recovers the first case: index.html is served `no-cache`, so the
 * fresh document points at the current hashes. The second case cannot be
 * recovered, so we reload at most once per short window — if the same failure
 * comes straight back we let the error through and the Sentry error boundary
 * renders a real failure state instead of the page reloading forever.
 */
export const PRELOAD_RELOAD_KEY = 'carousel_preload_reload_at'
export const PRELOAD_RELOAD_WINDOW_MS = 10_000

/**
 * True when a reload is worth attempting: we have never reloaded for a preload
 * failure, or the last attempt is old enough that it evidently recovered.
 */
export function shouldReloadForPreloadError(lastReloadAt: string | null, now: number): boolean {
  if (lastReloadAt === null) return true
  const at = Number(lastReloadAt)
  if (!Number.isFinite(at)) return true
  return now - at > PRELOAD_RELOAD_WINDOW_MS
}

export function initPreloadErrorHandler() {
  window.addEventListener('vite:preloadError', (event) => {
    let lastReloadAt: string | null
    try {
      lastReloadAt = sessionStorage.getItem(PRELOAD_RELOAD_KEY)
    } catch {
      // sessionStorage is unavailable (private mode, blocked storage). Without
      // it we cannot detect a loop, so leave the error to the boundary.
      return
    }

    if (!shouldReloadForPreloadError(lastReloadAt, Date.now())) return

    try {
      sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()))
    } catch {
      return
    }

    event.preventDefault()
    window.location.reload()
  })
}
